import React, { useEffect, useMemo, useState } from 'react'

import { useT } from '../../i18n'
import {
  clearCurrentOperatorSession,
  getCurrentOperatorSession,
  isPinEntryLocked,
  recordFailedPinAttempt,
  recordSuccessfulPinAttempt,
  setCurrentOperatorSession,
  type OperatorSession
} from '../../utils/operatorSession'
import { findOperatorByPin, refreshOperatorRoster } from '../../utils/operatorRoster'
import { recordCrewEvent } from './crewModeTelemetry'

// Crew Mode Charter D-4 shared identity mechanism — the reusable PIN-entry /
// switch-operator surface every Crew Mode capture screen mounts before
// showing its own capture flow. Built once here (per the #116/#117 "fix it
// once across all four P0 streams" precedent) rather than four times.
//
// Charter U-2 interaction budget for this control: 48x48dp minimum touch
// targets, 56x56dp for the digit keys and Enter (the most-repeated
// controls in this specific screen). Charter O-5: PIN verification is
// entirely local (via the cached roster + Web Crypto), so this never
// blocks on network — see operatorRoster.ts / operatorPinCrypto.ts.

const DIGIT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'] as const

const buttonBaseStyle: React.CSSProperties = {
  minWidth: 56,
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  fontWeight: 800,
  fontSize: 20,
  cursor: 'pointer'
}

export type OperatorGateProps = {
  children: React.ReactNode
}

/**
 * Renders `children` only once an operator session is active; otherwise
 * shows the PIN pad. Mount this at the top of any Crew Mode capture screen.
 */
export default function OperatorGate({ children }: OperatorGateProps) {
  const t = useT()
  const [session, setSession] = useState<OperatorSession | null>(() => getCurrentOperatorSession())
  const [pinDraft, setPinDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [locked, setLocked] = useState(() => isPinEntryLocked())

  useEffect(() => {
    // Opportunistic, fire-and-forget — never blocks rendering the PIN pad.
    // Charter O-5: this is a convenience refresh, not a requirement.
    void refreshOperatorRoster()
  }, [])

  const submitPin = async (pin: string) => {
    if (locked) return
    setVerifying(true)
    setError(null)
    try {
      const match = await findOperatorByPin(pin)
      if (!match) {
        recordFailedPinAttempt()
        setLocked(isPinEntryLocked())
        setError(t('crewMode.operatorGate.error.invalidPin'))
        setPinDraft('')
        return
      }

      recordSuccessfulPinAttempt()
      const nextSession = setCurrentOperatorSession({
        actorId: match.actorId,
        displayName: match.displayName,
        tenantMembershipId: match.tenantMembershipId
      })
      setSession(nextSession)
      setPinDraft('')
    } finally {
      setVerifying(false)
    }
  }

  const handleDigit = (digit: string) => {
    if (locked || verifying) return
    const next = (pinDraft + digit).slice(0, 4)
    setPinDraft(next)
    if (next.length === 4) {
      void submitPin(next)
    }
  }

  const handleBackspace = () => {
    if (verifying) return
    setPinDraft((prev) => prev.slice(0, -1))
  }

  const handleSwitchOperator = () => {
    recordCrewEvent('operator.switch')
    clearCurrentOperatorSession()
    setSession(null)
    setPinDraft('')
    setError(null)
  }

  const pinDotDisplay = useMemo(
    () => Array.from({ length: 4 }, (_, index) => (index < pinDraft.length ? '●' : '○')).join(' '),
    [pinDraft]
  )

  if (session) {
    return (
      <div>
        <div
          data-testid="operator-gate-active-badge"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderRadius: 12,
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            marginBottom: 12
          }}
        >
          <span style={{ fontWeight: 800, color: '#166534' }}>{session.displayName}</span>
          <button
            type="button"
            data-testid="operator-gate-switch"
            onClick={handleSwitchOperator}
            style={{ ...buttonBaseStyle, minHeight: 48, minWidth: 48, padding: '8px 16px', fontSize: 14 }}
          >
            {t('crewMode.operatorGate.switchOperator')}
          </button>
        </div>
        {children}
      </div>
    )
  }

  return (
    <div
      data-testid="operator-gate-pin-pad"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: 24,
        maxWidth: 320,
        margin: '0 auto'
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 16 }}>{t('crewMode.operatorGate.title')}</div>
      <div data-testid="operator-gate-pin-dots" style={{ fontSize: 28, letterSpacing: 8 }}>
        {pinDotDisplay}
      </div>
      {locked && (
        <div data-testid="operator-gate-locked" style={{ color: '#991b1b', fontWeight: 700, textAlign: 'center' }}>
          {t('crewMode.operatorGate.lockedOut')}
        </div>
      )}
      {!locked && error && (
        <div data-testid="operator-gate-error" style={{ color: '#991b1b', fontWeight: 700, textAlign: 'center' }}>
          {error}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 56px)',
          gap: 12
        }}
      >
        {DIGIT_KEYS.map((key, index) => {
          if (key === '') return <div key={`spacer-${index}`} />
          if (key === 'back') {
            return (
              <button
                key="back"
                type="button"
                data-testid="operator-gate-backspace"
                onClick={handleBackspace}
                disabled={locked || verifying}
                style={{ ...buttonBaseStyle, opacity: locked || verifying ? 0.5 : 1 }}
              >
                ⌫
              </button>
            )
          }
          return (
            <button
              key={key}
              type="button"
              data-testid={`operator-gate-digit-${key}`}
              onClick={() => handleDigit(key)}
              disabled={locked || verifying}
              style={{ ...buttonBaseStyle, opacity: locked || verifying ? 0.5 : 1 }}
            >
              {key}
            </button>
          )
        })}
      </div>
    </div>
  )
}
